/**
 * Trustpilot Review Widget List - Dropdown UI Component
 * Handles dropdown interactions for filter and sort dropdowns
 */

class TrustpilotReviewWidgetList extends HTMLElement {
  constructor() {
    super();

    this.mobileBreakpoint = 567;
    this.dropdowns = [];
    this.backdrop = null;
    this.activeDropdown = null;
    this.lastFocusedElement = null;
    this.focusTrapHandlers = {};
    this.dropdownTriggers = new Map(); // Store trigger for each dropdown
  }

  connectedCallback() {
    this.init();
  }

  /** Find all [data-dropdown], init each, set up backdrop + document click-to-close. */
  init() {
    const dropdownElements = this.querySelectorAll('[data-dropdown]');
    this.backdrop = this.querySelector('[data-dropdown-backdrop]');

    if (dropdownElements.length === 0) return;

    dropdownElements.forEach((dropdown) => {
      this.dropdowns.push(dropdown);
      this.initDropdown(dropdown);
    });

    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => this.closeAllDropdowns());
    }

    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-dropdown]') && !e.target.closest('[data-dropdown-backdrop]')) {
        this.closeAllDropdowns();
      }
    });
  }

  /** Per dropdown: bind trigger, options, close btn, Arrow keys, Enter/Space; call setRateFilter/setReviewSort on select. */
  initDropdown(dropdown) {
    const trigger = dropdown.querySelector('[data-dropdown-trigger]');
    const options = dropdown.querySelectorAll('[data-dropdown-option]');
    const closeBtn = dropdown.querySelector('[data-dropdown-close-btn]');
    const optionsList = dropdown.querySelector('[role="listbox"]');

    dropdown.addEventListener('mouseenter', () => {
      if (!this.isMobile()) {
        this.closeAllDropdowns();

        dropdown.classList.add('active');
        this.updateDropdownAria(dropdown, true);
      }
    });

    dropdown.addEventListener('mouseleave', () => {
      if (!this.isMobile()) {
        dropdown.classList.remove('active');
        this.updateDropdownAria(dropdown, false);
      }
    });

    if (trigger) {
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();

        const wasActive = dropdown.classList.contains('active');
        this.handleTriggerClick(dropdown, wasActive);
      });

      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          trigger.click();
        } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();

          if (!dropdown.classList.contains('active')) {
            this.handleTriggerClick(dropdown, false);
          }

          /** Focus will move to first/last option after opening */
          setTimeout(() => {
            const optionsArray = Array.from(options);
            if (optionsArray.length > 0) {
              if (e.key === 'ArrowDown') {
                /** Focus first option */
                this.focusFirstOption(dropdown);
              } else {
                /** Focus last option */
                const lastOption = optionsArray[optionsArray.length - 1];
                optionsArray.forEach(opt => {
                  opt.setAttribute('aria-selected', 'false');
                  opt.setAttribute('tabindex', '-1');
                });

                lastOption.setAttribute('aria-selected', 'true');
                lastOption.setAttribute('tabindex', '0');
                lastOption.focus();
              }
            }
          }, 50);
        }
      });
    }

    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        
        const trigger = dropdown.querySelector('[data-dropdown-trigger]');
        this.closeAllDropdowns(trigger);
      });

      closeBtn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();

          const trigger = dropdown.querySelector('[data-dropdown-trigger]');
          this.closeAllDropdowns(trigger);
        }
      });
    }

    /** Add keyboard navigation for dropdown container (Arrow Up/Down) */
    if (optionsList || dropdown) {
      const container = optionsList || dropdown;
      container.addEventListener('keydown', (e) => {
        if (!dropdown.classList.contains('active')) return;

        const optionsArray = Array.from(options);
        if (optionsArray.length === 0) return;

        /** Find currently focused or selected option */
        let selectedElem = optionsArray.find(opt => opt === document.activeElement);
        if (!selectedElem) {
          selectedElem = optionsArray.find(opt => opt.getAttribute('aria-selected') === 'true');
        }
        if (!selectedElem) {
          selectedElem = optionsArray.find(opt => opt.getAttribute('tabindex') !== '-1') || optionsArray[0];
        }

        const currentIndex = optionsArray.indexOf(selectedElem);
        const nextElem = optionsArray[currentIndex + 1];
        const prevElem = optionsArray[currentIndex - 1];

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          e.stopPropagation();

          if (nextElem) {
            selectedElem.setAttribute('aria-selected', 'false');
            selectedElem.setAttribute('tabindex', '-1');

            nextElem.setAttribute('aria-selected', 'true');
            nextElem.setAttribute('tabindex', '0');
            nextElem.focus();
          }
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          e.stopPropagation();

          if (prevElem) {
            selectedElem.setAttribute('aria-selected', 'false');
            selectedElem.setAttribute('tabindex', '-1');

            prevElem.setAttribute('aria-selected', 'true');
            prevElem.setAttribute('tabindex', '0');
            prevElem.focus();
          }
        } else if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          this.closeAllDropdowns(trigger);
        } else if (e.key === 'Tab') {
          e.stopPropagation();
          this.closeAllDropdowns();
        }
      });
    }

    options.forEach((option) => {
      option.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          option.click();
        } else if (e.key === 'Tab') {
          e.stopPropagation();
          this.closeAllDropdowns();
        }
      });

      option.addEventListener('click', (e) => {
        e.stopPropagation();
        this.handleOptionSelection(option, dropdown);
      });
    });
  }

  /** Open/close dropdown; on mobile enable backdrop and focus trap. */
  handleTriggerClick(dropdown, wasActive) {
    const trigger = dropdown.querySelector('[data-dropdown-trigger]');
    
    if (this.isMobile()) {
      this.closeAllDropdowns();
      if (!wasActive) {
        this.lastFocusedElement = document.activeElement;

        /** Store trigger for this dropdown */
        this.dropdownTriggers.set(dropdown, trigger);
        dropdown.classList.add('active');
        this.activeDropdown = dropdown;
        this.updateDropdownAria(dropdown, true);

        if (this.backdrop) this.backdrop.classList.add('active');
        document.body.style.overflow = 'hidden';
        document.body.classList.add('tp-dropdown-active');

        setTimeout(() => {
          this.focusFirstOption(dropdown);
          this.trapFocus(dropdown);
        }, 50);
      }
    } else {
      if (wasActive) {
        this.closeAllDropdowns();
      } else {
        this.closeAllDropdowns();
        /** Store trigger for this dropdown */
        this.dropdownTriggers.set(dropdown, trigger);
        dropdown.classList.add('active');
        this.updateDropdownAria(dropdown, true);

        /** Focus first option when dropdown opens */
        setTimeout(() => {
          this.focusFirstOption(dropdown);
        }, 50);
      }
    }
  }

  focusFirstOption(dropdown) {
    const options = dropdown.querySelectorAll('[data-dropdown-option]');
    if (options.length > 0) {
      const firstOption = options[0];
      /** Set first option as selected and focusable */
      options.forEach(opt => {
        opt.setAttribute('aria-selected', 'false');
        opt.setAttribute('tabindex', '-1');
      });

      firstOption.setAttribute('aria-selected', 'true');
      firstOption.setAttribute('tabindex', '0');
      firstOption.focus();
    }
  }

  /** Update selected option UI and call trustpilotReviewsLoader.setRateFilter or setReviewSort. */
  handleOptionSelection(option, dropdown) {
    const value = option.getAttribute('data-value');
    const isAlreadySelected = option.classList.contains('selected');
    const dropdownType = dropdown.getAttribute('data-dropdown-type');
    const trigger = dropdown.querySelector('[data-dropdown-trigger]');
    
    /** For rating filter: toggle behavior - clicking selected option clears filter */
    if (dropdownType === 'rate' && isAlreadySelected) {
      /** Deselect the option */
      option.classList.remove('selected');
      option.setAttribute('aria-selected', 'false');

      /** Clear the selected text */
      const selectedTextItems = dropdown.querySelectorAll('[data-selected-text]');
      selectedTextItems.forEach((item) => {
        item.textContent = '';
      });

      if (window.trustpilotReviewsLoader) {
        window.trustpilotReviewsLoader.setRateFilter('');
      }

      this.closeAllDropdowns(trigger);
      return;
    }

    /** Normal selection behavior */
    let text = '';

    /** For rating filter: extract star symbols and append " star" or " stars" */
    if (dropdownType === 'rate') {
      const mobileText = option.querySelector('.mobile-only-text');
      if (mobileText) {
        const labelSpans = mobileText.querySelectorAll('span');
        if (labelSpans.length > 0) {
          const stars = labelSpans[labelSpans.length - 1].textContent.trim();
          const starText = parseInt(value) === 1 ? 'Star' : 'Stars';
          text = stars + ' ' + starText;
        }
      }
    } else {
      /** For sort options: get text without icons */
      const clone = option.cloneNode(true);
      /** Remove all icon spans */
      clone.querySelectorAll('.icon-check, .desktop-only-text').forEach((el) => el.remove());
      text = clone.textContent.trim();
    }

    const selectedTextItems = dropdown.querySelectorAll('[data-selected-text]');
    selectedTextItems.forEach((item) => {
      item.textContent = text;
    });

    const options = dropdown.querySelectorAll('[data-dropdown-option]');
    options.forEach((opt) => {
      opt.classList.remove('selected');
      opt.setAttribute('aria-selected', 'false');
    });

    option.classList.add('selected');
    option.setAttribute('aria-selected', 'true');

    if (window.trustpilotReviewsLoader) {
      if (dropdownType === 'rate') {
        window.trustpilotReviewsLoader.setRateFilter(value);
      } else if (dropdownType === 'sort') {
        window.trustpilotReviewsLoader.setReviewSort(value);
      }
    }

    this.closeAllDropdowns(trigger);
  }

  /** Close all dropdowns, remove backdrop, return focus to trigger if provided. */
  closeAllDropdowns(focusTrigger = null) {
    this.removeFocusTrap();

    this.dropdowns.forEach((dropdown) => {
      dropdown.classList.remove('active');
      const trigger = dropdown.querySelector('[data-dropdown-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');

      const dropdownContent = dropdown.querySelector('.dropdown-content');
      if (dropdownContent) {
        dropdownContent.setAttribute('aria-hidden', 'true');
      }

      const focusableElements = dropdown.querySelectorAll(
        '[data-dropdown-option], [data-dropdown-close-btn]'
      );

      focusableElements.forEach((el) => {
        el.setAttribute('tabindex', '-1');
      });
    });

    if (this.backdrop) this.backdrop.classList.remove('active');
    document.body.style.overflow = '';
    document.body.classList.remove('tp-dropdown-active');

    /** Prioritize focusing the trigger if provided (for ADA compliance) */
    if (focusTrigger) {
      setTimeout(() => {
        focusTrigger.focus();
      }, 0);
      this.lastFocusedElement = null;
    } else if (this.lastFocusedElement) {
      this.lastFocusedElement.focus();
      this.lastFocusedElement = null;
    }

    this.activeDropdown = null;
    /** Clear stored triggers */
    this.dropdownTriggers.clear();
  }

  /** Set aria-expanded on trigger and aria-hidden on content; set tabindex on options. */
  updateDropdownAria(dropdown, isOpen) {
    const trigger = dropdown.querySelector('[data-dropdown-trigger]');
    if (trigger) trigger.setAttribute('aria-expanded', isOpen);

    const dropdownContent = dropdown.querySelector('.dropdown-content');
    if (dropdownContent) {
      if (isOpen) {
        dropdownContent.removeAttribute('aria-hidden');
      } else {
        dropdownContent.setAttribute('aria-hidden', 'true');
      }
    }

    const focusableElements = dropdown.querySelectorAll(
      '[data-dropdown-option], [data-dropdown-close-btn]'
    );
    focusableElements.forEach((el) => {
      el.setAttribute('tabindex', isOpen ? '0' : '-1');
    });
  }

  /** Helpers */
  isMobile() {
    return window.innerWidth <= this.mobileBreakpoint;
  }

  getFocusableElements(container) {
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex^="-"])',
      '[role="button"]',
      '[data-dropdown-option]',
      '[data-dropdown-close-btn]',
    ].join(', ');

    return Array.from(container.querySelectorAll(focusableSelectors)).filter(
      (el) =>
        !el.hasAttribute('aria-hidden') &&
        el.offsetParent !== null &&
        el.getAttribute('tabindex') !== '-1'
    );
  }

  /** Keep focus inside dropdown (mobile); Tab cycles, Escape closes. */
  trapFocus(container) {
    const elements = this.getFocusableElements(container);
    if (elements.length === 0) return;

    const first = elements[0];
    const last = elements[elements.length - 1];
    const focusTarget = first;

    this.removeFocusTrap();

    this.focusTrapHandlers.focusin = (event) => {
      if (!container.contains(event.target) && container.classList.contains('active')) {
        event.preventDefault();
        event.stopPropagation();
        focusTarget.focus();
      }
    };

    this.focusTrapHandlers.keydown = (event) => {
      if (!container.classList.contains('active')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        
        const trigger = container.querySelector('[data-dropdown-trigger]');
        this.closeAllDropdowns(trigger);
        return;
      }

      /** Allow Tab to close dropdown and move focus outside */
      if (event.key === 'Tab') {
        /** Don't prevent default - let Tab work normally */
        this.closeAllDropdowns();
        return;
      }

      /** Arrow keys are handled by the container listener */
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        return;
      }

      const currentElements = this.getFocusableElements(container);
      if (currentElements.length === 0) return;

      const currentFirst = currentElements[0];
      const currentLast = currentElements[currentElements.length - 1];
      const activeElement = document.activeElement;
      const currentIndex = currentElements.indexOf(activeElement);

      if (event.shiftKey) {
        if (currentIndex <= 0 || activeElement === currentFirst) {
          event.preventDefault();
          currentLast.focus();
        }
      } else {
        if (currentIndex >= currentElements.length - 1 || activeElement === currentLast) {
          event.preventDefault();
          currentFirst.focus();
        }
      }
    };

    document.addEventListener('focusin', this.focusTrapHandlers.focusin, true);
    document.addEventListener('keydown', this.focusTrapHandlers.keydown, true);

    focusTarget.focus();
  }

  /** Remove focus trap listeners. */
  removeFocusTrap() {
    if (this.focusTrapHandlers.focusin) {
      document.removeEventListener('focusin', this.focusTrapHandlers.focusin, true);
    }
    if (this.focusTrapHandlers.keydown) {
      document.removeEventListener('keydown', this.focusTrapHandlers.keydown, true);
    }
    this.focusTrapHandlers = {};
  }
}

if (!customElements.get('trustpilot-review-widget-list')) {
  customElements.define('trustpilot-review-widget-list', TrustpilotReviewWidgetList);
}
